import { useEffect, useState } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Download } from "lucide-react";
import { API } from "@/context/AuthContext";
import { inr } from "@/pages/Dashboard";

const BUCKETS = [
  { key: "only_sap", label: "Open in SAP, not in ageing" },
  { key: "not_in_sap", label: "In ageing, no longer open in SAP" },
  { key: "mismatch", label: "Amount differs" },
  { key: "after_as_on", label: "Dated after As-on" },
  { key: "outside_scope", label: "Outside ageing scope" },
];

const csvOf = (d) => {
  const head = ["Bucket", "Document", "FY", "Item", "Doc Type", "Party Code", "Party", "Reference", "Posting Date", "Invoice Date", "Special G/L", "Amount (INR)", "Ageing Amount (INR)", "Reason"];
  const rows = BUCKETS.flatMap((b) => (d[b.key] || []).map((r) => [b.label, r.doc, r.fiscal_year, r.item, r.doc_type, r.party_code, r.party_name || "", r.ref_no || "", r.posting_date || "", r.invoice_date || "", r.special_gl || "", r.amount, r.ageing_amount ?? "", r.reason]));
  return [head, ...rows].map((r) => r.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
};

export const TBGapDialog = ({ side, group, asOn, onClose, testPrefix }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("only_sap");
  useEffect(() => {
    if (!group) return;
    setData(null); setError("");
    const params = { gl_account: group.gl_account, ...(asOn && asOn !== "today" ? { as_on: asOn } : {}) };
    axios.get(`${API}/reports/${side}-ageing/tb-reconciliation/gap`, { params })
      .then((r) => { setData(r.data); const first = BUCKETS.find((b) => (r.data[b.key] || []).length); setTab(first ? first.key : "only_sap"); })
      .catch((e) => setError(e.response?.data?.detail || "Could not load the gap documents"));
  }, [group, asOn, side]);

  const download = () => {
    const blob = new Blob([csvOf(data)], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = `tb_gap_${data.gl_account}_${data.as_on}.csv`; a.click(); URL.revokeObjectURL(a.href);
  };

  const rows = data ? data[tab] || [] : [];
  return (
    <Dialog open={!!group} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-5xl max-h-[85vh] overflow-hidden flex flex-col" data-testid={`${testPrefix}-tb-gap-dialog`}>
        <DialogHeader>
          <DialogTitle className="text-base">Documents behind the gap — {group?.name} <span className="font-mono text-slate-400 text-sm">{group?.gl_account}</span></DialogTitle>
          <DialogDescription asChild>
            <div className="text-xs text-slate-500">
              {data ? <>Live SAP open items {inr(data.sap_open_live)} ({data.sap_open_docs} docs) vs extracted {inr(data.extracted_open)} ({data.extracted_docs} docs) → gap <b className={Math.abs(data.gap) <= 1 ? "text-emerald-700" : "text-amber-700"}>{inr(data.gap)}</b>, explained by the lists below {inr(data.explained)}. Last extraction {data.last_sync ? new Date(data.last_sync).toLocaleString() : "—"}.</> : error ? <span className="text-red-600" data-testid={`${testPrefix}-tb-gap-error`}>{error}</span> : "Reading open items from SAP…"}
            </div>
          </DialogDescription>
        </DialogHeader>
        {data && (
          <>
            <div className="flex flex-wrap items-center gap-1.5">
              {BUCKETS.map((b) => (
                <button key={b.key} type="button" onClick={() => setTab(b.key)} data-testid={`${testPrefix}-tb-gap-tab-${b.key}`}
                  className={`rounded-full px-3 py-1 text-[11px] font-medium border transition-colors ${tab === b.key ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"}`}>
                  {b.label} · {data.counts[b.key]}{data.counts[b.key] ? ` · ${inr(data.sums[b.key])}` : ""}
                </button>
              ))}
              <button type="button" onClick={download} data-testid={`${testPrefix}-tb-gap-download`} className="ml-auto inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2.5 py-1 text-[11px] font-medium text-slate-700 hover:bg-slate-50"><Download className="h-3 w-3" />CSV</button>
            </div>
            <div className="overflow-auto flex-1 rounded-md border border-slate-200">
              <table className="w-full text-xs" data-testid={`${testPrefix}-tb-gap-table`}>
                <thead className="sticky top-0 bg-slate-50">
                  <tr className="text-slate-500 uppercase tracking-wider text-[10px] border-b border-slate-200">
                    <th className="text-left py-2 px-2">Document</th><th className="text-left py-2 px-2">Type</th><th className="text-left py-2 px-2">Party</th>
                    <th className="text-left py-2 px-2">Reference</th><th className="text-left py-2 px-2">Posted</th><th className="text-right py-2 px-2">Amount</th>
                    {tab === "mismatch" && <th className="text-right py-2 px-2">In ageing</th>}<th className="text-left py-2 px-2">Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 && <tr><td colSpan={8} className="py-6 text-center text-slate-400">Nothing in this list</td></tr>}
                  {rows.map((r, i) => (
                    <tr key={`${r.doc}-${r.item}-${i}`} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`${testPrefix}-tb-gap-row`}>
                      <td className="py-1.5 px-2 font-mono text-slate-800">{r.doc}<span className="text-slate-400">/{r.fiscal_year}{r.item ? `/${r.item}` : ""}</span></td>
                      <td className="py-1.5 px-2">{r.doc_type}{r.special_gl ? <span className="ml-1 text-slate-400">({r.special_gl})</span> : null}</td>
                      <td className="py-1.5 px-2 text-slate-700">{r.party_name || r.party_code}</td>
                      <td className="py-1.5 px-2 text-slate-600">{r.ref_no}</td>
                      <td className="py-1.5 px-2 text-slate-600">{r.posting_date}</td>
                      <td className="py-1.5 px-2 text-right tabular-nums font-semibold">{inr(r.amount)}</td>
                      {tab === "mismatch" && <td className="py-1.5 px-2 text-right tabular-nums">{inr(r.ageing_amount)}</td>}
                      <td className="py-1.5 px-2 text-slate-600">{r.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
